import { useMemo } from 'react'

import { SanityPrintType } from '@data/sanity/queries/types/print'
import { SanityShopPrint } from '@data/sanity/queries/types/shop'
import { SanityPrintConfiguratorStrings } from '@data/sanity/queries/types/strings'
import { useGetFormattedPrice } from '@lib/helpers'
import { getImageTypeRadioItems } from '@lib/print/image'
import { EnabledStatus } from '@lib/print/types'

import Checkbox from '@components/checkbox/alternative'
import Radio from '@components/radio'
import PrintFieldItems from './print-field-items'

interface PrintFieldProps {
  fieldEnabledStatus: EnabledStatus | null
  fieldValue: SanityShopPrint | null
  prints: SanityShopPrint[]
  printAssetType: SanityPrintType
  printConfiguratorStrings: SanityPrintConfiguratorStrings
  showLogoPrints?: boolean
  showImagePrints?: boolean
  logoPrintFeePrice?: number
  imagePrintFeePrice?: number
  onPrintClick: (print: SanityShopPrint | null) => void
  onPrintAssetTypeChange?: (printType: SanityPrintType) => void
  onEnabledStatusChange: (newStatus: EnabledStatus) => void
}

const PrintField = ({
  fieldEnabledStatus,
  fieldValue,
  prints,
  printAssetType,
  printConfiguratorStrings,
  showLogoPrints = true,
  showImagePrints = true,
  logoPrintFeePrice,
  imagePrintFeePrice,
  onPrintClick,
  onPrintAssetTypeChange,
  onEnabledStatusChange,
}: PrintFieldProps) => {
  const getFormattedPrice = useGetFormattedPrice()

  const logoPrints = useMemo(() => {
    return prints.filter((print) => print.type === 'logo') ?? []
  }, [prints])

  const imagePrints = useMemo(() => {
    return prints.filter((print) => print.type === 'image') ?? []
  }, [prints])

  const fieldTitle = useMemo(() => {
    if (onPrintAssetTypeChange) {
      return printConfiguratorStrings.printConfiguratorUnitedPrintsTitle
    }

    if (!onPrintAssetTypeChange && printAssetType === 'logo') {
      const formattedLogoPrintFeePrice =
        typeof logoPrintFeePrice !== 'undefined'
          ? ` (+${getFormattedPrice(logoPrintFeePrice)})`
          : ''
      return `${printConfiguratorStrings.printConfiguratorLogoPrintTitle} ${formattedLogoPrintFeePrice}`
    }

    if (!onPrintAssetTypeChange && printAssetType === 'image') {
      const formattedImagePrintFeePrice =
        typeof imagePrintFeePrice !== 'undefined'
          ? ` (+${getFormattedPrice(imagePrintFeePrice)})`
          : ''
      return `${printConfiguratorStrings.printConfiguratorImagePrintTitle} ${formattedImagePrintFeePrice}`
    }
  }, [
    getFormattedPrice,
    imagePrintFeePrice,
    logoPrintFeePrice,
    onPrintAssetTypeChange,
    printAssetType,
    printConfiguratorStrings,
  ])

  const handleToggleField = (newChecked: boolean) => {
    if (newChecked) {
      onEnabledStatusChange('yes')
    }

    if (!newChecked) {
      onEnabledStatusChange('no')

      // Clear selected print
      onPrintClick(null)
    }
  }

  return (
    <div className="flex flex-col gap-y-5">
      <Checkbox
        checked={fieldEnabledStatus === 'yes'}
        setChecked={handleToggleField}
      >
        <div className="leading-tight font-semibold">{fieldTitle}</div>
      </Checkbox>

      {fieldEnabledStatus === 'yes' && (
        <>
          {!!onPrintAssetTypeChange && (
            <Radio
              value={printAssetType}
              items={getImageTypeRadioItems(
                getFormattedPrice,
                printConfiguratorStrings,
                showLogoPrints && logoPrints.length > 0,
                showImagePrints && imagePrints.length > 0,
                logoPrintFeePrice,
                imagePrintFeePrice,
              )}
              onChange={(value) => {
                onPrintAssetTypeChange(value as SanityPrintType)
              }}
            />
          )}

          {printAssetType == 'logo' &&
            showLogoPrints &&
            logoPrints.length > 0 && (
              <PrintFieldItems
                prints={logoPrints}
                fieldValue={fieldValue}
                onPrintClick={onPrintClick}
              />
            )}

          {printAssetType == 'image' &&
            showImagePrints &&
            imagePrints.length > 0 && (
              <PrintFieldItems
                prints={imagePrints}
                fieldValue={fieldValue}
                onPrintClick={onPrintClick}
              />
            )}
        </>
      )}
    </div>
  )
}

export default PrintField
